import { useState } from "react";

const COUNTRIES = [
  { value: "CA", label: "Canada" },
  { value: "US", label: "United States" },
];

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
];

const ALLOWED_LANGUAGES = ["en", "fr", "es"];

const US_ZIP_RE = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_RE = /^[A-CEGHJ-NPR-TVXY]\d[A-CEGHJ-NPR-TV-Z][ -]?\d[A-CEGHJ-NPR-TV-Z]\d$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS_RE = /^\+?\d{10,15}$/;

function clientValidate(form) {
  const errs = {};
  if (form.name.trim().length < 2) errs.name = "Please enter your full name.";
  if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email.";
  const phoneDigits = form.phone.replace(/[\s().-]/g, "");
  if (!PHONE_DIGITS_RE.test(phoneDigits)) errs.phone = "Please enter a valid phone number.";
  if (form.country !== "US" && form.country !== "CA") {
    errs.country = "Please select USA or Canada.";
  } else if (form.country === "US") {
    if (!US_ZIP_RE.test(form.postal.trim())) errs.postal = "ZIP should be 5 digits (e.g. 90210) or 9 with a dash.";
    if (form.healthcareProvider.trim().length < 2) errs.healthcareProvider = "Please tell us your healthcare provider.";
  } else if (form.country === "CA") {
    if (!CA_POSTAL_RE.test(form.postal.trim())) errs.postal = "Postal code should look like K1A 0B1.";
  }
  if (!ALLOWED_LANGUAGES.includes(form.language)) errs.language = "Please choose a language.";
  return errs;
}

const initial = {
  name: "",
  email: "",
  phone: "",
  country: "CA",
  postal: "",
  healthcareProvider: "",
  language: "en",
};

export default function Pilot() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState(false);

  function update(name, value) {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) {
      setErrors((e) => {
        const { [name]: _drop, ...rest } = e;
        return rest;
      });
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setServerError("");
    const localErrs = clientValidate(form);
    if (Object.keys(localErrs).length > 0) {
      setErrors(localErrs);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/pilot/signup", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.errors) setErrors(data.errors);
        else setServerError(data.error || "Something went wrong. Please try again.");
        return;
      }
      setSuccess(true);
      setForm(initial);
    } catch (err) {
      setServerError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <section className="section">
        <div className="container">
          <div className="form-card">
            <div className="alert-success">
              <strong>Thanks for signing up!</strong> We've added you to the
              pilot list and will be in touch soon with next steps.
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setSuccess(false)}
              type="button"
            >
              Sign up another person
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="hero">
        <div className="container">
          <h1>Join the MyJava.ai pilot</h1>
          <p className="lead">
            We're inviting a small group of older adults and their families to
            try MyJava.ai's peer support sessions. The pilot is currently open
            in the <strong>USA</strong> and <strong>Canada</strong> — other
            countries coming soon.
          </p>
          <p className="lead">
            We are developing this technology in cooperation with{" "}
            <a href="https://triluna.ai" target="_blank" rel="noopener noreferrer">
              triluna.ai
            </a>.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <form className="form-card" onSubmit={onSubmit} noValidate>
            <div className="notice">
              The pilot is currently for the <strong>USA</strong> and{" "}
              <strong>Canada</strong> only. Other countries coming soon — we'll
              keep you posted.
            </div>

            {serverError && <div className="alert-error">{serverError}</div>}

            <div className="field">
              <label htmlFor="f-name">Full name</label>
              <input
                id="f-name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                required
              />
              {errors.name && <div className="error">{errors.name}</div>}
            </div>

            <div className="field">
              <label htmlFor="f-email">Email</label>
              <input
                id="f-email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                required
              />
              {errors.email && <div className="error">{errors.email}</div>}
            </div>

            <div className="field">
              <label htmlFor="f-phone">Phone number</label>
              <input
                id="f-phone"
                type="tel"
                autoComplete="tel"
                placeholder="(555) 555-5555"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                required
              />
              {errors.phone && <div className="error">{errors.phone}</div>}
            </div>

            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "var(--c-ink-soft)",
                  marginBottom: 6,
                  padding: 0,
                }}
              >
                Country
              </legend>
              <div className="radio-row">
                {COUNTRIES.map((c) => (
                  <label key={c.value}>
                    <input
                      type="radio"
                      name="country"
                      value={c.value}
                      checked={form.country === c.value}
                      onChange={() => update("country", c.value)}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
              {errors.country && <div className="error">{errors.country}</div>}
            </fieldset>

            <div className="field">
              <label htmlFor="f-postal">
                {form.country === "CA" ? "Postal code" : "ZIP code"}
              </label>
              <input
                id="f-postal"
                type="text"
                autoComplete="postal-code"
                placeholder={form.country === "CA" ? "K1A 0B1" : "90210"}
                value={form.postal}
                onChange={(e) => update("postal", e.target.value)}
                required
              />
              {errors.postal && <div className="error">{errors.postal}</div>}
            </div>

            {form.country === "US" && (
              <div className="field">
                <label htmlFor="f-provider">Healthcare provider</label>
                <input
                  id="f-provider"
                  type="text"
                  value={form.healthcareProvider}
                  onChange={(e) => update("healthcareProvider", e.target.value)}
                  required
                />
                <div className="help">
                  e.g. your insurer, Medicare Advantage plan, or primary clinic.
                </div>
                {errors.healthcareProvider && (
                  <div className="error">{errors.healthcareProvider}</div>
                )}
              </div>
            )}

            <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "var(--c-ink-soft)",
                  marginBottom: 6,
                  padding: 0,
                }}
              >
                Preferred language
              </legend>
              <div className="radio-row">
                {LANGUAGES.map((l) => (
                  <label key={l.value}>
                    <input
                      type="radio"
                      name="language"
                      value={l.value}
                      checked={form.language === l.value}
                      onChange={() => update("language", l.value)}
                    />
                    {l.label}
                  </label>
                ))}
              </div>
              {errors.language && <div className="error">{errors.language}</div>}
              {(form.language === "fr" || form.language === "es") && (
                <div
                  className="notice"
                  style={{
                    marginTop: 10,
                    background: "#FFF8E1",
                    borderColor: "#F0D27A",
                    color: "#5C4A12",
                  }}
                >
                  MyJava.ai sessions are currently in English only.{" "}
                  {form.language === "fr" ? "French" : "Spanish"} sessions are
                  coming soon — please sign up anyway and we'll let you know
                  as soon as your language is available.
                </div>
              )}
            </fieldset>

            <button className="btn" type="submit" disabled={submitting}>
              {submitting ? "Sending..." : "Apply to the pilot"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
